import { CopyOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Alert, Badge, Button, Layout, Table } from 'antd';
import type {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import type { AlignType } from 'rc-table/es/interface';
import { useContext, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { dateTimeCell } from 'app/components/lists/Cells/Cells';
import { ListPage } from 'app/components/lists/ListPage';
import { Pagination } from 'app/components/lists/Pagination';
import { Header } from 'app/components/lists/TableHeader';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { useSEOLandings } from 'app/hooks/data/seoLandings/useSEOLandings';
import type { SEOLandingList, SEOLandingStatus } from 'app/typings/SEOLanding';
import { useCopyToClipboard } from 'app/utils/clipboard';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { scrollToTop } from 'app/utils/scroll';
import type { SortOrder } from 'app/utils/sort';
import { convertFromAntdSortOrder } from 'app/utils/sort';

import { ModalCheckBox } from '../Extranet/ExtranetBookings/ModalCheckBox';

import './SEOLandings.scss';

const ALIGN_CENTER = 'center' as AlignType;

export type LandingsListQueryParam = {
  search: string;
  pageNumber: string;
  sorting: SortOrder;
  publishedFilter: boolean[];
};

const initialQueryParam: LandingsListQueryParam = {
  search: '',
  pageNumber: '1',
  sorting: 'descend',
  publishedFilter: [true, false],
};

export const StatusBadge = (published: SEOLandingStatus) =>
  published === 'published' ? (
    <Badge status="success" text="Published" />
  ) : (
    <Badge status="warning" text="Unpublished" />
  );

export const SEOLandings = () => {
  const { copied, copyToClipboardWithAlert } = useCopyToClipboard();
  const location = useLocation();
  const history = useHistory();
  const config = useContext(ConfigContext);
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const search = query.get('search') || initialQueryParam.search;

  const pageNumber = parseInt(
    query.get('pageNumber') || initialQueryParam.pageNumber,
    10
  );

  const publishedFilter = useMemo(() => {
    const filter = query.get('publishedFilter');

    return filter
      ? filter.split(',').map((val) => val === 'true')
      : [true, false];
  }, [query]);

  const sorting = query.get('sorting') as SortOrder;

  const debouncedSearch = useDebounce(search, 500);

  const { data: landings, isFetching } = useSEOLandings({
    search: debouncedSearch,
    pageNumber,
    sorting,
    isPublished: publishedFilter.length === 1 ? publishedFilter[0] : undefined,
  });

  if (!landings) {
    return null;
  }

  const handleOffset = (x: number) => {
    scrollToTop();
    updateQueryParams({ pageNumber: (pageNumber + x).toString() });
  };

  const handleSearch = (nextSearch: string) => {
    updateQueryParams({ search: nextSearch, pageNumber: '1' });
  };

  const handleRowActions = (landing: SEOLandingList) => ({
    onClick: () => navigateToEditLanding(landing.slug),
  });

  const updateQueryParams = (params: Record<string, string>) => {
    const oldParams = new URLSearchParams(location.search);
    const newParams = { ...Object.fromEntries(oldParams.entries()), ...params };

    const updatedParams = new URLSearchParams(newParams);

    history.push(`${location.pathname}?${updatedParams.toString()}`);
  };

  const handleIsPublishedFilter = (value: boolean[]) => {
    if (value.length === 0) {
      value.push(!publishedFilter?.[publishedFilter.length - 1]);
    }

    updateQueryParams({ publishedFilter: value.join(','), pageNumber: '1' });
  };

  const handleOnChange = (
    _: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SEOLandingList> | SorterResult<SEOLandingList>[]
  ) => {
    // @ts-ignore -- legacy directive, needs investigation
    const { field, order } = sorter;

    const adaptedOrder = convertFromAntdSortOrder(order);

    updateQueryParams({
      publishedFilter: filters.published ? filters.published.join(',') : '',
      sorting: field === 'name' ? adaptedOrder : initialQueryParam.sorting,
      pageNumber: '1',
    });
  };

  const publishedColumnTitle = (
    <ModalCheckBox
      title="Status"
      defaultValue={[true, false]}
      onChange={handleIsPublishedFilter}
      items={[
        {
          value: true,
          label: 'Published',
        },
        {
          value: false,
          label: 'Unpublished',
        },
      ]}
      value={publishedFilter}
    />
  );

  const navigateToEditLanding = (landingSlug: string) => {
    history.push(`/seo-landings/${encodeURIComponent(landingSlug)}/edit`);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'slug',
      align: 'left' as AlignType,
      sorter: (a: SEOLandingList, b: SEOLandingList) =>
        a.slug.localeCompare(b.slug),
      width: 350,
    },
    {
      title: publishedColumnTitle,
      dataIndex: 'status',
      render: StatusBadge,
      align: 'left' as AlignType,
      width: 128,
    },
    {
      title: 'Last modified',
      dataIndex: 'updatedAt',
      render: dateTimeCell('YYYY-MM-DD HH:mm:ss'),
      align: ALIGN_CENTER,
      sorter: (a: SEOLandingList, b: SEOLandingList) =>
        a.updatedAt.localeCompare(b.updatedAt),
      width: 180,
    },
    {
      title: 'URL',
      dataIndex: 'slug',
      align: 'left' as AlignType,
      ellipsis: true,
      render: (slug: string) => {
        const url = `${config?.appUrl}${slug}`;

        return (
          <div className="url-cell-container">
            <span className="url-cell-text" title={url}>
              {url}
            </span>

            <Button
              icon={<CopyOutlined />}
              onClick={(e) => copyToClipboardWithAlert(e, url)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <ListPage title="SEO Landings">
      {copied && (
        <Alert
          className="alert-copyToClipBoard"
          type="success"
          message="URL successfully copied"
          icon={<InfoCircleFilled />}
          showIcon
        />
      )}
      <Layout className="landing">
        <Table
          dataSource={landings}
          pagination={false}
          title={() => (
            <Header
              handleSearch={handleSearch}
              searchValue={search}
              pageNumber={pageNumber}
              pageResultsLength={landings?.length}
              handleOffset={handleOffset}
            />
          )}
          loading={isFetching}
          rowKey="slug"
          onRow={handleRowActions}
          columns={columns}
          onChange={handleOnChange}
        />
        <Pagination
          className="footer"
          pageNumber={pageNumber}
          handleOffset={handleOffset}
          pageResultsLength={landings?.length}
        />
      </Layout>
    </ListPage>
  );
};
